import { useConversationMessageMediaContentState } from '@modules/conversation/ConversationMediaContentView/useConversationMessageMediaContent';
import { useConversationEditorConfig } from '@modules/conversation/hooks/useConversationEditorConfig';
import { ConversationMediaContextProvider } from '@modules/conversation/providers/ConversationMedia';
import { type EditorOptions } from '@tiptap/react';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { IChatConversation, IChatUserPermissions } from 'src/@types/chat';
import {
  ConversationMessageContentType,
  IConversationMessage,
} from 'src/@types/conversation';
import { useMediaPreviewModal } from 'src/features/MediaPreview';
import { MediaMessageReturn } from 'src/features/MediaPreview/types';
import { useLocales } from 'src/locales';
import { useConversationContext } from 'src/provider/ConversationContext';
import { SoundBoardData } from 'src/requests/soundboard';
import { useChatAllowedAction } from 'src/sections/@dashboard/chat/shared/useChatAllowedAction';
import { chatFileType } from 'src/sections/@dashboard/chat/shared/utils';
import { useListener } from 'src/services/EventBus';
import { useMentionableParticipants } from './useMentionableParticipants';
import { useSendMessage } from './useSendMessage';

type State = {
  conversation: IChatConversation;
  replyTo: IConversationMessage | null;
  setReplyTo(message: IConversationMessage | null): void;
  goToMessage: string | undefined;
  setGoToMessage(messageId: string | undefined): void;
  loadGoTo: boolean;
  setLoadGoTo(load: boolean): void;
  handleTextMessage(message: string): void;
  handleFileSelect(type: string, file: File): void;
  onSendSoundBoard: (sound: Partial<SoundBoardData>) => void;
  editorConfig: Partial<EditorOptions>;
  userPermissions: IChatUserPermissions;
  searchTerm: string;
  setSearchTerm(searchTerm: string): void;
  scrollEnd: boolean;
  showInput: boolean;
};

const ChatConversationContext = createContext<State>({} as any);

export const useChatConversationContext = () =>
  useContext(ChatConversationContext);

export const ChatConversationContextProvider = ({
  children,
  conversation,
}: {
  children: ReactNode;
  conversation: IChatConversation;
}) => {
  const { translate } = useLocales();
  const {
    replyTo,
    setReplyTo,
    goToMessage,
    setGoToMessage,
    loadGoTo,
    setLoadGoTo,
    searchTerm,
    setSearchTerm,
    scrollEnd,
    setScrollEnd,
    setShowInput,
    showInput,
  } = useConversationContext();

  const userPermissions = useChatAllowedAction({ conversation });

  const { handleFileSelect, handleSendMessage, handleTextMessage } =
    useSendMessage(conversation?._id);

  const { setMediaContent } = useConversationMessageMediaContentState();
  const { showMediaPreviewModal } = useMediaPreviewModal();

  const handleScrollEnd = useCallback(() => {
    setScrollEnd(true);
    setTimeout(() => setScrollEnd(false), 200);
  }, [setScrollEnd]);

  const onSendFile = useCallback(
    async (
      contentType: ConversationMessageContentType,
      file: File,
      content: string,
    ) => {
      const message = await handleFileSelect(file);

      setMediaContent(message.mediaRef, {
        mediaContent: URL.createObjectURL(file),
        percentageLoaded: 0,
      });

      handleSendMessage({
        ...message,
        content,
        contentType,
        replyTo: replyTo?._id,
      });

      setReplyTo(null);
      handleScrollEnd();
    },
    [
      handleFileSelect,
      setMediaContent,
      handleSendMessage,
      replyTo?._id,
      handleScrollEnd,
      setReplyTo,
    ],
  );

  const handleSendMedia = useCallback(
    async ({ message, file, type }: MediaMessageReturn) => {
      setShowInput(true);
      await onSendFile(type, file, message);
    },
    [onSendFile, setShowInput],
  );

  const handleChatUploadFile = async (file: File, text?: string) => {
    handleOpenMediaPreviewModal(chatFileType(file.type), file);
  };

  const { mentionableParticipants } = useMentionableParticipants(
    conversation?._id,
    conversation?.parentId,
  );

  const { editorConfig } = useConversationEditorConfig({
    handleChatUploadFile,
    placeholder: translate('shared.editor,conversation_placeholder'),
    participants: mentionableParticipants,
  });

  useListener(
    'ShareScreenRecord',
    (event) => {
      handleOpenMediaPreviewModal(
        ConversationMessageContentType.VIDEO,
        event.file,
      );
    },
    [conversation?._id],
  );

  const handleOpenMediaPreviewModal = useCallback(
    (type: ConversationMessageContentType, file: File) => {
      setShowInput(false);
      showMediaPreviewModal({
        file,
        setShowInput,
        editorConfig,
        conversationId: conversation._id,
        sendMedia: async (response: Omit<MediaMessageReturn, 'type'>) => {
          await handleSendMedia({ ...response, type });
        },
      });
    },
    [
      showMediaPreviewModal,
      handleSendMedia,
      setShowInput,
      conversation?._id,
      editorConfig,
    ],
  );

  const processFileSelection = useCallback(
    (type: ConversationMessageContentType, file: File) => {
      if (file.name.toLowerCase().endsWith('.gif')) {
        return onSendFile(type, file, '');
      }
      if (
        type === ConversationMessageContentType.VIDEO ||
        type === ConversationMessageContentType.IMAGE
      ) {
        return handleOpenMediaPreviewModal(type, file);
      }
      return onSendFile(type, file, '');
    },
    [handleOpenMediaPreviewModal, onSendFile],
  );

  const value = useMemo(
    () => ({
      conversation,
      replyTo,
      setReplyTo,
      goToMessage,
      setGoToMessage,
      loadGoTo,
      setLoadGoTo,
      editorConfig,
      searchTerm,
      setSearchTerm,
      handleTextMessage: (message: string) => {
        handleSendMessage({
          ...handleTextMessage(message),
          replyTo: replyTo?._id,
        });

        setReplyTo(null);
        handleScrollEnd();
      },
      onSendSoundBoard: (sound: Partial<SoundBoardData>) => {
        handleSendMessage({
          channel: conversation._id,
          mediaRef: sound._id,
          contentType: ConversationMessageContentType.SOUNDBOARD,
          soundBoard: {
            soundId: sound._id,
            volume: sound.volume,
            icon: sound.icon,
          },
          replyTo: replyTo?._id,
        });

        setReplyTo(null);
        handleScrollEnd();
      },
      handleFileSelect: processFileSelection,
      userPermissions,
      scrollEnd,
      showInput,
    }),
    [
      conversation,
      replyTo,
      goToMessage,
      loadGoTo,
      editorConfig,
      searchTerm,
      userPermissions,
      scrollEnd,
      handleSendMessage,
      handleTextMessage,
      handleScrollEnd,
      setReplyTo,
      setGoToMessage,
      setLoadGoTo,
      setSearchTerm,
      showInput,
      processFileSelection,
    ],
  );

  if (!conversation?._id) return null;

  return (
    <ChatConversationContext.Provider value={value}>
      <ConversationMediaContextProvider>
        {children}
      </ConversationMediaContextProvider>
    </ChatConversationContext.Provider>
  );
};
